import { api } from './client';
import type { AuthUser } from './auth';
import type { Difficulty, SerializedBoardDto } from './puzzles';

export interface LeaderboardEntry {
  rank: number;
  leaderboard_name: string;
  score: number;
  moves_used: number;
  won: boolean;
  life_id: string;
  is_me: boolean;
}

export interface LeaderboardView {
  date: string;
  difficulty: Difficulty;
  total_players: number;
  entries: LeaderboardEntry[];
  me: { rank: number; score: number } | null;
  /** False while the viewer could still profit from seeing boards. */
  can_view_plays: boolean;
}

export interface LeaderboardPlay {
  leaderboard_name: string;
  score: number;
  won: boolean;
  moves_used: number;
  words: Array<{ word: string; score: number }>;
  board: SerializedBoardDto;
}

export async function fetchLeaderboard(
  date: string,
  difficulty: Difficulty,
): Promise<LeaderboardView> {
  const res = await api.get(`/api/leaderboard/${date}/${difficulty}`);
  return res.data;
}

export async function fetchLeaderboardPlay(
  date: string,
  difficulty: Difficulty,
  life_id: string,
): Promise<LeaderboardPlay> {
  const res = await api.get(`/api/leaderboard/${date}/${difficulty}/plays/${life_id}`);
  return res.data;
}

export async function updateLeaderboardName(name: string): Promise<AuthUser> {
  const res = await api.patch('/api/me/leaderboard-name', { name });
  return res.data.user;
}

export async function randomiseLeaderboardName(): Promise<AuthUser> {
  const res = await api.post('/api/me/leaderboard-name/randomise');
  return res.data.user;
}

export interface PublicEntry {
  rank: number;
  leaderboard_name: string;
  score: number;
  moves_used: number;
  won: boolean;
}

export interface PublicLeaderboardView {
  date: string;
  difficulty: Difficulty;
  total_players: number;
  top: PublicEntry[];
  /** Where the guest's score would place, or null if they haven't played. */
  would_rank: number | null;
}

export async function fetchPublicLeaderboard(
  date: string,
  difficulty: Difficulty,
  score?: number,
): Promise<PublicLeaderboardView> {
  const res = await api.get(`/api/leaderboard/${date}/${difficulty}/public`, {
    params: score === undefined ? undefined : { score },
  });
  return res.data;
}
